import { useContext, useState } from 'react';
import { ethers } from 'ethers';
import { Web3Context } from '../context/Web3Context';
import { CONTRACT_ADDRESS } from '../config/contract';

const useContract = () => {
  const { contract, usdtContract, userId, account, showNotification, refreshUserData } = useContext(Web3Context);
  const [loading, setLoading] = useState(false);

  const approveUSDT = async (amount) => {
    if (!usdtContract) throw new Error('USDT contract not loaded');
    const tx = await usdtContract.approve(CONTRACT_ADDRESS, amount);
    showNotification('Approving USDT... Please wait.', 'info', 0);
    await tx.wait();
    showNotification('USDT approved!', 'success');
  };

  const checkAllowance = async (amount) => {
    if (!usdtContract || !account) return false;
    const allowance = await usdtContract.allowance(account, CONTRACT_ADDRESS);
    return allowance.gte(amount);
  };

  const getUSDTBalance = async () => {
    if (!usdtContract || !account) return ethers.BigNumber.from(0);
    return await usdtContract.balanceOf(account);
  };

  const register = async (referrerId) => {
    if (!contract) throw new Error('Contract not loaded');
    setLoading(true);
    try {
      const price = await contract.packagePrices(0);
      const hasAllowance = await checkAllowance(price);
      if (!hasAllowance) {
        await approveUSDT(ethers.constants.MaxUint256);
      }
      showNotification('Registering... Please confirm the transaction.', 'info', 0);
      const tx = await contract.register(referrerId);
      showNotification('Transaction submitted. Waiting for confirmation...', 'info', 0);
      await tx.wait();
      showNotification('Registration successful!', 'success');
      await refreshUserData();
    } catch (err) {
      const msg = err.reason || err.data?.message || err.message || 'Registration failed';
      showNotification(msg, 'error');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const upgradePackage = async (packageId) => {
    if (!contract) throw new Error('Contract not loaded');
    setLoading(true);
    try {
      const price = await contract.packagePrices(packageId - 1);
      const hasAllowance = await checkAllowance(price);
      if (!hasAllowance) {
        await approveUSDT(ethers.constants.MaxUint256);
      }
      showNotification('Upgrading package... Please confirm.', 'info', 0);
      const tx = await contract.upgradePackage(packageId);
      showNotification('Transaction submitted. Waiting...', 'info', 0);
      await tx.wait();
      showNotification('Package upgraded successfully!', 'success');
      await refreshUserData();
    } catch (err) {
      const msg = err.reason || err.data?.message || err.message || 'Upgrade failed';
      showNotification(msg, 'error');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const retopupPackage = async (packageId) => {
    if (!contract) throw new Error('Contract not loaded');
    setLoading(true);
    try {
      const price = await contract.packagePrices(packageId - 1);
      const hasAllowance = await checkAllowance(price);
      if (!hasAllowance) {
        await approveUSDT(ethers.constants.MaxUint256);
      }
      showNotification('Re-topping up... Please confirm.', 'info', 0);
      const tx = await contract.retopupPackage(packageId);
      showNotification('Transaction submitted. Waiting...', 'info', 0);
      await tx.wait();
      showNotification('Re-topup successful!', 'success');
      await refreshUserData();
    } catch (err) {
      const msg = err.reason || err.data?.message || err.message || 'Retopup failed';
      showNotification(msg, 'error');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const claimIncome = async () => {
    if (!contract) throw new Error('Contract not loaded');
    setLoading(true);
    try {
      showNotification('Claiming income... Please confirm.', 'info', 0);
      const tx = await contract.claimIncome();
      showNotification('Transaction submitted. Waiting...', 'info', 0);
      await tx.wait();
      showNotification('Income claimed successfully!', 'success');
      await refreshUserData();
    } catch (err) {
      const msg = err.reason || err.data?.message || err.message || 'Claim failed';
      showNotification(msg, 'error');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const claimAllPoolIncomes = async () => {
    if (!contract) throw new Error('Contract not loaded');
    setLoading(true);
    try {
      showNotification('Claiming all incomes... Please confirm.', 'info', 0);
      const tx = await contract.claimAllPoolIncomes();
      showNotification('Transaction submitted. Waiting...', 'info', 0);
      await tx.wait();
      showNotification('All incomes claimed successfully!', 'success');
      await refreshUserData();
    } catch (err) {
      const msg = err.reason || err.data?.message || err.message || 'Claim failed';
      showNotification(msg, 'error');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const claimPoolIncome = async (poolType) => {
    if (!contract) throw new Error('Contract not loaded');
    setLoading(true);
    const poolNames = ['', 'Gold', 'Platinum', 'Diamond', 'Crown', 'Blue', 'Ambassador'];
    try {
      showNotification(`Claiming ${poolNames[poolType]} pool income...`, 'info', 0);
      let tx;
      if (poolType === 1) tx = await contract.claimGoldPoolIncome();
      else if (poolType === 2) tx = await contract.claimPlatinumPoolIncome();
      else if (poolType === 3) tx = await contract.claimDiamondPoolIncome();
      else if (poolType === 4) tx = await contract.claimCrownPoolIncome();
      else if (poolType === 5) tx = await contract.claimBluePoolIncome();
      else if (poolType === 6) tx = await contract.claimAmbassadorPoolIncome();
      showNotification('Transaction submitted. Waiting...', 'info', 0);
      await tx.wait();
      showNotification(`${poolNames[poolType]} pool income claimed!`, 'success');
      await refreshUserData();
    } catch (err) {
      const msg = err.reason || err.data?.message || err.message || 'Pool claim failed';
      showNotification(msg, 'error');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Admin function
  const adminCall = async (op, a1, a2) => {
    if (!contract) throw new Error('Contract not loaded');
    setLoading(true);
    try {
      showNotification('Executing admin operation...', 'info', 0);
      const tx = await contract.p(op, a1 || 0, a2 || 0);
      showNotification('Transaction submitted. Waiting...', 'info', 0);
      await tx.wait();
      showNotification('Admin operation successful!', 'success');
    } catch (err) {
      const msg = err.reason || err.data?.message || err.message || 'Admin operation failed';
      showNotification(msg, 'error');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const getUserDetails = async (uid) => {
    if (!contract) return null;
    const targetId = uid || userId;
    if (!targetId) return null;
    const data = await contract.getUserDetails(targetId);
    return {
      account: data.account,
      id: data.id.toNumber(),
      referrer: data.referrer.toNumber(),
      isActive: data.isActive,
      totalDirectReferrals: data.totalDirectReferrals.toNumber(),
      claimableAmount: ethers.utils.formatEther(data.claimableAmount),
      totalClaimed: ethers.utils.formatEther(data.totalClaimed),
      totalIncome: ethers.utils.formatEther(data.totalIncome),
    };
  };

  const getUserPackages = async (uid) => {
    if (!contract) return [];
    const targetId = uid || userId;
    if (!targetId) return [];
    return await contract.getUserPackages(targetId);
  };

  const getUserPackageDetails = async (uid, packageId) => {
    if (!contract) return null;
    const targetId = uid || userId;
    if (!targetId) return null;
    const data = await contract.getUserPackageDetails(targetId, packageId);
    return {
      isActive: data.isActive,
      activationTime: data.activationTime.toNumber(),
      totalInvested: ethers.utils.formatEther(data.totalInvested),
      totalEarned: ethers.utils.formatEther(data.totalEarned),
      ceilingLimit: ethers.utils.formatEther(data.ceilingLimit),
      remainingLimit: ethers.utils.formatEther(data.remainingLimit),
    };
  };

  const getIncomeBreakdown = async (uid) => {
    if (!contract) return null;
    const targetId = uid || userId;
    if (!targetId) return null;
    const data = await contract.getUserIncomeBreakdown(targetId);
    return {
      sponsorIncome: ethers.utils.formatEther(data.sponsorIncome),
      levelIncome: ethers.utils.formatEther(data.levelIncome),
      goldPoolIncome: ethers.utils.formatEther(data.goldPoolIncome),
      platinumPoolIncome: ethers.utils.formatEther(data.platinumPoolIncome),
      diamondPoolIncome: ethers.utils.formatEther(data.diamondPoolIncome),
      crownPoolIncome: ethers.utils.formatEther(data.crownPoolIncome),
      bluePoolIncome: ethers.utils.formatEther(data.bluePoolIncome),
      ambassadorPoolIncome: ethers.utils.formatEther(data.ambassadorPoolIncome),
      totalIncome: ethers.utils.formatEther(data.totalIncome),
      claimableAmount: ethers.utils.formatEther(data.claimableAmount),
      totalClaimed: ethers.utils.formatEther(data.totalClaimed),
    };
  };

  const getReferrals = async (uid) => {
    if (!contract) return [];
    const targetId = uid || userId;
    if (!targetId) return [];
    const refs = await contract.getUserReferrals(targetId);
    return refs.map((r) => r.toNumber());
  };

  const getPoolEligibilities = async (uid) => {
    if (!contract) return null;
    const targetId = uid || userId;
    if (!targetId) return null;
    const data = await contract.getPoolEligibilities(targetId);
    return {
      gold: data.goldEligible,
      platinum: data.platinumEligible,
      diamond: data.diamondEligible,
      crown: data.crownEligible,
      blue: data.blueEligible,
      ambassador: data.ambassadorEligible,
    };
  };

  const getClaimablePoolIncomes = async (uid) => {
    if (!contract) return null;
    const targetId = uid || userId;
    if (!targetId) return null;
    const data = await contract.getUserClaimablePoolIncomes(targetId);
    return {
      gold: ethers.utils.formatEther(data.goldClaimable),
      platinum: ethers.utils.formatEther(data.platinumClaimable),
      diamond: ethers.utils.formatEther(data.diamondClaimable),
      crown: ethers.utils.formatEther(data.crownClaimable),
      blue: ethers.utils.formatEther(data.blueClaimable),
      ambassador: ethers.utils.formatEther(data.ambassadorClaimable),
    };
  };

  const getMatrixDetails = async (uid, packageId) => {
    if (!contract) return null;
    const targetId = uid || userId;
    if (!targetId) return null;
    const data = await contract.getMatrixDetails(targetId, packageId);
    return {
      upline: data.upline.toNumber(),
      directDownlines: data.directDownlines.map((d) => d.toNumber()),
      totalTeamSize: data.totalTeamSize.toNumber(),
    };
  };

  const getMatrixLevelCounts = async (uid, packageId) => {
    if (!contract) return [];
    const targetId = uid || userId;
    if (!targetId) return [];
    const counts = await contract.getMatrixLevelCounts(targetId, packageId);
    return counts.map((c) => c.toNumber());
  };

  const getMatrixLevelUsers = async (uid, packageId, level) => {
    if (!contract) return [];
    const targetId = uid || userId;
    if (!targetId) return [];
    const users = await contract.getMatrixLevelUsers(targetId, packageId, level);
    return users.map((u) => u.toNumber());
  };

  const getUserActivities = async (uid) => {
    if (!contract) return [];
    const targetId = uid || userId;
    if (!targetId) return [];
    const activities = await contract.getUserActivities(targetId);
    return activities.map((a) => ({
      id: a.id.toNumber(),
      userId: a.userId.toNumber(),
      activityType: a.activityType.toNumber(),
      amount: ethers.utils.formatEther(a.amount),
      packageId: a.packageId.toNumber(),
      fromId: a.fromId.toNumber(),
      timestamp: a.timestamp.toNumber(),
    }));
  };

  const getTotalUsers = async () => {
    if (!contract) return 0;
    const total = await contract.getTotalUsers();
    return total.toNumber();
  };

  const getPoolAccumulations = async () => {
    if (!contract) return null;
    const data = await contract.getPoolAccumulations();
    return {
      gold: ethers.utils.formatEther(data.gold),
      platinum: ethers.utils.formatEther(data.platinum),
      diamond: ethers.utils.formatEther(data.diamond),
      crown: ethers.utils.formatEther(data.crown),
      blue: ethers.utils.formatEther(data.blue),
      ambassador: ethers.utils.formatEther(data.ambassador),
    };
  };

  const getPoolEligibleCounts = async () => {
    if (!contract) return null;
    const data = await contract.getPoolEligibleCounts();
    return {
      gold: data[0].toNumber(),
      platinum: data[1].toNumber(),
      diamond: data[2].toNumber(),
      crown: data[3].toNumber(),
      blue: data[4].toNumber(),
      ambassador: data[5].toNumber(),
    };
  };

  const getOwner = async () => {
    if (!contract) return null;
    return await contract.owner();
  };

  const getAdmin = async () => {
    if (!contract) return null;
    return await contract.admin();
  };

  const getContractBalance = async () => {
    if (!contract) return '0';
    const bal = await contract.getContractBalance();
    return ethers.utils.formatEther(bal);
  };

  const getMissingPool = async () => {
    if (!contract) return '0';
    const val = await contract.missingIncomePool();
    return ethers.utils.formatEther(val);
  };

  return {
    loading,
    register,
    upgradePackage,
    retopupPackage,
    claimIncome,
    claimAllPoolIncomes,
    claimPoolIncome,
    adminCall,
    approveUSDT,
    checkAllowance,
    getUSDTBalance,
    getUserDetails,
    getUserPackages,
    getUserPackageDetails,
    getIncomeBreakdown,
    getReferrals,
    getPoolEligibilities,
    getClaimablePoolIncomes,
    getMatrixDetails,
    getMatrixLevelCounts,
    getMatrixLevelUsers,
    getUserActivities,
    getTotalUsers,
    getPoolAccumulations,
    getPoolEligibleCounts,
    getOwner,
    getAdmin,
    getContractBalance,
    getMissingPool,
  };
};

export default useContract;
